import { NgModule, APP_INITIALIZER, ErrorHandler } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { SharedModule } from './shared/shared.module';
import { ChatFabModule } from './shared/chat-fab/chat-fab.module';
import { ChatModalModule } from './shared/chat-modal/chat-modal.module';
import { AuthModule } from './auth/auth.module';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { SpinnerComponent } from './core/components/spinner/spinner.component';
import { HttpClientModule, HttpClient, HTTP_INTERCEPTORS } from '@angular/common/http';
import { UserStatusInterceptor } from './core/interceptor/user-status.interceptor';
import { AuthInterceptor } from './core/interceptor/auth.interceptor';
import { AuthErrorInterceptor } from './core/interceptor/auth-error.interceptor';
import { InflightDedupeInterceptor } from './core/interceptor/inflight-dedupe.interceptor';
import { NgrokInterceptor } from './core/interceptor/ngrok.interceptor';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { MsalRedirectComponent } from '@azure/msal-angular';
import { MatDialogModule } from '@angular/material/dialog';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MAT_DATE_FORMATS, MatNativeDateModule } from '@angular/material/core';
import { FormsModule } from '@angular/forms';
import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { MatSidenavModule } from '@angular/material/sidenav';
import { CommonModule } from '@angular/common';
import { NgChartsModule } from 'ng2-charts';
import { registerLocaleData } from '@angular/common';
import localeEs from '@angular/common/locales/es';
import localePt from '@angular/common/locales/pt';
import { defineLocale } from 'ngx-bootstrap/chronos';
import { esLocale } from 'ngx-bootstrap/locale';
import { enGbLocale } from 'ngx-bootstrap/locale';
import { frLocale } from 'ngx-bootstrap/locale';
import { ptBrLocale } from 'ngx-bootstrap/locale';
import { zhCnLocale } from 'ngx-bootstrap/locale';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { createMatDateFormats } from './core/utils/date-format';
import { MatIconModule } from '@angular/material/icon';
import { UserLocaleService } from './services/user-locale.service';
import { GA4Provider } from './analytics/providers/ga4.provider';
import { ANALYTICS_PROVIDERS } from './analytics/models/analytics.token';
import { ConsoleProvider } from './analytics/providers/console.provider';
import { SentryErrorHandler } from './core/sentry/sentry-error-handler';
import { Router } from '@angular/router';
import * as Sentry from '@sentry/angular';
import { SentryHttpErrorInterceptor } from './core/sentry/sentry-http-error.interceptor';
import { CafiWelcomeComponent } from './shared/cafi-welcome/cafi-welcome.component';
import { environment } from 'src/environments/environment';
import { DateFormatLocalizationService } from './core/services/date-format-localization.service';

defineLocale('es', { ...esLocale, week: { dow: 1, doy: 4 } });
defineLocale('en', { ...enGbLocale, week: { dow: 1, doy: 4 } });
defineLocale('fr', { ...frLocale, week: { dow: 1, doy: 4 } });
defineLocale('pt', { ...ptBrLocale, week: { dow: 1, doy: 4 } });
defineLocale('zh-cn', { ...zhCnLocale, week: { dow: 1, doy: 4 } });

registerLocaleData(localeEs, 'es');
registerLocaleData(localePt, 'pt');

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

export function matDateFormatsFactory(
  dateFormatLocalization: DateFormatLocalizationService
) {
  return createMatDateFormats(dateFormatLocalization.getCurrentConfig().displayFormat);
}

@NgModule({
  declarations: [AppComponent, SpinnerComponent, CafiWelcomeComponent],
  imports: [
    BrowserModule,
    CommonModule,
    AppRoutingModule,
    MatDialogModule,
    MatIconModule,
    MatSlideToggleModule,
    MatNativeDateModule,
    SharedModule,
    ChatFabModule,
    ChatModalModule,
    AuthModule,
    HttpClientModule,
    BrowserAnimationsModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    FormsModule,
    StoreModule.forRoot({}, {}),
    EffectsModule.forRoot([]),
    MatSidenavModule,
    NgChartsModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatInputModule,
  ],
  exports: [SpinnerComponent],
  providers: [
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: InflightDedupeInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: NgrokInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: UserStatusInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: NgrokInterceptor, multi: true },
    {
      provide: MAT_DATE_FORMATS,
      useFactory: matDateFormatsFactory,
      deps: [DateFormatLocalizationService]
    },
    ...(environment.production
      ? [
        { provide: ANALYTICS_PROVIDERS, useClass: GA4Provider, multi: true },
      ]
      : [
        { provide: ANALYTICS_PROVIDERS, useClass: ConsoleProvider, multi: true },
      ]),

    { provide: HTTP_INTERCEPTORS, useClass: AuthErrorInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: SentryHttpErrorInterceptor, multi: true },
    { provide: ErrorHandler, useClass: SentryErrorHandler },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => { },
      deps: [Sentry.TraceService],
      multi: true,
    },
  ],
  bootstrap: [AppComponent, MsalRedirectComponent],
})
export class AppModule {
  constructor() { }
}
